<template>
  <div>
    <b-modal
      :id="id"
      :ref="id"
      v-model="internalShow"
      :cancel-title-html="cancelButtonText"
      :ok-title-html="confirmButtonText"
      :title="title"
      :v-bind="$attrs"
      :no-close-on-backdrop="persistent"
      :no-close-on-esc="persistent"
      centered
      cancel-variant="outline-primary border-2"
      button-size="md"
      content-class="rounded-xl p-2 p-sm-3 shadow-lg text-dark"
      :body-class="bodyClass"
      :footer-class="footerClass"
      :header-class="headerClass"
      :ok-variant="okVariant"
      title-class="h2 font-weight-bold text-dark"
      @close="$emit('cancel')"
      @cancel="$emit('cancel')"
      @hide="onHide"
      @ok="$emit('confirm')"
    >
      <template #modal-title>
        <span v-bind="testIds.title">{{ title }}</span>
      </template>
      <p v-bind="testIds.message" class="my-1">{{ message }}</p>
      <!-- These slots exist only to hang a locator off each button: b-modal renders the footer
           buttons itself and exposes no way to set attributes on them. v-html matches the
           ok-title-html props they use by default, so callers passing markup keep working. -->
      <template #modal-cancel>
        <!-- eslint-disable-next-line vue/no-v-html-->
        <span v-bind="testIds.dismiss" v-html="cancelButtonText"></span>
      </template>
      <template #modal-ok>
        <!-- eslint-disable-next-line vue/no-v-html-->
        <span v-bind="testIds.confirm" v-html="confirmButtonText"></span>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { testIdAttr } from '../../utils/testIdPolicy';

export default {
  name: 'DpConfirmationModal',
  props: {
    id: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      required: true,
    },
    confirmButtonText: {
      type: String,
      default: 'Confirm',
    },
    cancelButtonText: {
      type: String,
      default: 'Cancel',
    },
    // Defaults to false on purpose: removing the escape hatch strands keyboard and
    // screen-reader users. Opt in only where losing the dialog would lose work.
    persistent: {
      type: Boolean,
      default: false,
    },
    okVariant: {
      type: String,
      default: 'danger',
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    dividers: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['confirm', 'cancel'],
  data() {
    return {
      internalShow: this.show,
    };
  },
  computed: {
    headerClass() {
      return this.dividers ? 'pb-3 h1' : 'border-bottom-0 pb-0 h1';
    },
    bodyClass() {
      return this.dividers ? 'py-3' : 'pt-0';
    },
    footerClass() {
      return [
        'mt-4 mt-sm-0 d-flex flex-column-reverse items-flex-grow align-items-stretch',
        this.stacked ? 'dp-modal-footer-stacked' : 'flex-md-row',
        this.dividers ? 'pt-3' : 'border-top-0',
      ].join(' ');
    },
    // Derived from the required `id` so every confirmation dialog gets addressable
    // buttons without each call site restating them.
    testIds() {
      return {
        title: testIdAttr(`${this.id}-title`),
        message: testIdAttr(`${this.id}-message`),
        confirm: testIdAttr(`${this.id}-confirm-button`),
        dismiss: testIdAttr(`${this.id}-dismiss-button`),
      };
    },
  },
  methods: {
    // `hide` fires for every dismissal reason, including ok/cancel which already emit above.
    // Only Escape and backdrop clicks need translating into the cancel contract here.
    onHide(event) {
      if (event.trigger === 'esc' || event.trigger === 'backdrop') {
        this.$emit('cancel');
      }
    },
  },
};
</script>

<style lang="scss">
.rounded-xl {
  border-radius: 1.25rem !important;
}

.d-flex.items-flex-grow > * {
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
}

// Bootstrap's .modal-footer offsets children with margin-left; stacked buttons
// need that reset or they sit indented.
.dp-modal-footer-stacked > :not(:first-child) {
  margin-left: 0;
  margin-bottom: 1rem;
}
</style>
